import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react'
import type {
  PropertyType,
  EventType,
  TimelineEvent,
  HistoryFilters,
  HistorySortConfig,
  HistoryState,
  HistoryDataParams,
} from '../types'
import { historyService } from '../services/historyService'

interface HistoryContextValue extends HistoryState {
  propertyType: PropertyType
  setPropertyType: (type: PropertyType) => void
  loadHistory: (params: HistoryDataParams) => Promise<void>
  updateFilters: (filters: Partial<HistoryFilters>) => void
  updateSort: (sort: HistorySortConfig) => void
  selectEvent: (event: TimelineEvent | null) => void
  exportData: (format: 'csv' | 'excel') => Promise<void>
  filteredEvents: TimelineEvent[]
  eventCounts: Record<EventType, number>
}

const HistoryContext = createContext<HistoryContextValue | undefined>(undefined)

export const useHistory = () => {
  const context = useContext(HistoryContext)
  if (!context) {
    throw new Error('useHistory must be used within HistoryProvider')
  }
  return context
}

interface HistoryProviderProps {
  children: React.ReactNode
  propertyType: PropertyType
}

export const HistoryProvider: React.FC<HistoryProviderProps> = ({
  children,
  propertyType: initialPropertyType,
}) => {
  const [propertyType, setPropertyType] =
    useState<PropertyType>(initialPropertyType)
  const [state, setState] = useState<HistoryState>({
    events: [],
    loading: false,
    error: null,
    filters: {
      eventTypes: ['transaction', 'sale', 'rental', 'permit'],
    },
    sort: {
      field: 'date',
      direction: 'desc',
    },
    selectedEvent: null,
  })

  /**
   * Load history data from API
   */
  const loadHistory = useCallback(async (params: HistoryDataParams) => {
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      const events = await historyService.fetchAllHistory(params)
      setState((prev) => ({
        ...prev,
        events,
        loading: false,
      }))
    } catch (error) {
      console.error('Error loading history:', error)
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load history data',
      }))
    }
  }, [])

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters: Partial<HistoryFilters>) => {
    setState((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        ...newFilters,
      },
    }))
  }, [])

  /**
   * Update sort configuration
   */
  const updateSort = useCallback((sort: HistorySortConfig) => {
    setState((prev) => ({ ...prev, sort }))
  }, [])

  /**
   * Select an event for detail view
   */
  const selectEvent = useCallback((event: TimelineEvent | null) => {
    setState((prev) => ({ ...prev, selectedEvent: event }))
  }, [])

  /**
   * Export history data
   */
  const exportData = useCallback(async (format: 'csv' | 'excel') => {
    await historyService.exportHistory(filteredEvents, format)
  }, [])

  /**
   * Filter events based on current filters
   */
  const filteredEvents = useMemo(() => {
    let events = [...state.events]

    // Filter by event types
    if (state.filters.eventTypes.length > 0) {
      events = events.filter((event) =>
        state.filters.eventTypes.includes(event.type)
      )
    }

    // Filter by date range
    if (state.filters.date_range) {
      const { start, end } = state.filters.date_range
      events = events.filter((event) => {
        const eventDate = new Date(event.date)
        const startDate = new Date(start)
        const endDate = new Date(end)
        return eventDate >= startDate && eventDate <= endDate
      })
    }

    // Filter by search query
    if (state.filters.search_query) {
      const query = state.filters.search_query.toLowerCase()
      events = events.filter((event) => {
        const searchableText = [
          event.title,
          event.description,
          JSON.stringify(event.metadata),
        ]
          .join(' ')
          .toLowerCase()
        return searchableText.includes(query)
      })
    }

    // Sort events
    events.sort((a, b) => {
      let comparison = 0

      switch (state.sort.field) {
        case 'date': {
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime()
          break
        }
        case 'type': {
          comparison = a.type.localeCompare(b.type)
          break
        }
        case 'price': {
          const priceA = (a.metadata as any).price || 0
          const priceB = (b.metadata as any).price || 0
          comparison = priceA - priceB
          break
        }
      }

      return state.sort.direction === 'desc' ? -comparison : comparison
    })

    return events
  }, [state.events, state.filters, state.sort])

  /**
   * Calculate event counts by type
   */
  const eventCounts = useMemo(() => {
    const counts: Record<EventType, number> = {
      transaction: 0,
      sale: 0,
      rental: 0,
      permit: 0,
    }

    state.events.forEach((event) => {
      counts[event.type]++
    })

    return counts
  }, [state.events])

  const value: HistoryContextValue = {
    ...state,
    propertyType,
    setPropertyType,
    loadHistory,
    updateFilters,
    updateSort,
    selectEvent,
    exportData,
    filteredEvents,
    eventCounts,
  }

  return (
    <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>
  )
}
